import * as React from 'react';
import type { ILinkProps } from '@fluentui/react/lib/Link';
import { KeyCodes } from '@fluentui/react/lib/Utilities';

export default function useEditableLink(
    title: string | null | undefined,
    onTitleChanged: (title: string) => void
): [ILinkProps, () => void] {
    const [editable, setEditable] = React.useState(false);

    const linkRef = React.useRef<HTMLDivElement>(null);
    const timeStamp = React.useRef(0);

    const onMouseDown = React.useCallback(
        (e: React.MouseEvent) => {
            timeStamp.current = e.timeStamp;
        },
        [timeStamp]
    );

    const onRename = React.useCallback(() => {
        setEditable(true);
        linkRef.current?.focus();
        selectContents(linkRef.current);
    }, [setEditable]);

    const onClick = React.useCallback(
        (e: React.MouseEvent) => {
            // If you click and hold we enter editable mode
            const delay = e.timeStamp - timeStamp.current;
            const element = e.target as HTMLElement;
            if (delay > 500) {
                e.preventDefault();
                e.stopPropagation();
                setEditable(true);
                selectContents(element);
            }
        },
        [setEditable, timeStamp]
    );

    const commit = React.useCallback(
        (element: HTMLElement) => {
            const newValue = (element.innerText || '').trim();
            if (!newValue) {
                element.innerText = title || '';
            } else if (title != newValue) {
                onTitleChanged(newValue);
            }
            selectContents(null);
            setEditable(false);
        },
        [title, onTitleChanged, setEditable]
    );

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            timeStamp.current = e.timeStamp;
            const element = e.target as HTMLElement;
            switch (e.keyCode) {
                case KeyCodes.f2: // F2 enters editable mode
                    if (!editable) {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditable(true);
                        selectContents(element);
                    }
                    break;
                case KeyCodes.escape: // ESC reverts any changes and exits editable mode
                    if (editable) {
                        element.innerText = title || '';
                        selectContents(null);
                        setEditable(false);
                    }
                    break;
                case KeyCodes.enter: // ENTER commits changes and exits editable mode
                    if (editable) {
                        e.preventDefault();
                        e.stopPropagation();
                        commit(element);
                    }
                    break;
            }
        },
        [timeStamp, editable, setEditable, title, commit]
    );

    const onBlur = React.useCallback(
        (e: React.FocusEvent) => {
            if (editable) {
                const element = e.target as HTMLElement;
                commit(element);
            }
        },
        [editable, commit]
    );

    return [
        {
            contentEditable: editable,
            suppressContentEditableWarning: true,
            ref: linkRef,
            onMouseDown,
            onKeyDown,
            onClick,
            onBlur,
            role: editable ? 'textbox' : 'link',
        } as ILinkProps,
        onRename,
    ];
}

function selectContents(node: Node | null) {
    const sel = window.getSelection();
    if (sel) {
        sel.removeAllRanges();
        if (node) {
            var range = document.createRange();
            range.selectNodeContents(node);
            sel.addRange(range);
        }
    }
}
